import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Upload, CheckCircle, AlertTriangle, ArrowLeft, FileText, ChevronRight } from "lucide-react";

const CHANNEL_OPTIONS = [
  { value: "efectivo_bbva", label: "Efectivo — BBVA (Excel)", accept: ".xlsx,.xls" },
  { value: "tarjeta_clip", label: "Tarjeta — CLIP (CSV)", accept: ".csv" },
  { value: "rappi", label: "Rappi (CSV)", accept: ".csv,.xlsx" },
  { value: "uber_eats", label: "Uber Eats (CSV)", accept: ".csv,.xlsx" },
  { value: "didi", label: "DiDi (CSV)", accept: ".csv,.xlsx" },
  { value: "oxxo_pay", label: "Oxxo Pay (CSV)", accept: ".csv,.xlsx" },
];

export default function OpsConciliacionImport() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1); // 1=config, 2=preview, 3=done
  const [channel, setChannel] = useState("efectivo_bbva");
  const [branchId, setBranchId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importId, setImportId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const selectedChannel = CHANNEL_OPTIONS.find(c => c.value === channel);

  const handleUpload = async () => {
    if (!file) { setError("Selecciona un archivo."); return; }
    setLoading(true); setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("channel", channel);
      if (branchId) formData.append("branch_id", branchId);
      const res = await api.post("/ops/bank-movements/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
      setImportId(res.data.import_id);
      setStep(2);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al procesar el archivo.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true); setError("");
    try {
      const formData = new FormData();
      formData.append("import_id", importId);
      const res = await api.post("/ops/bank-movements/import/confirm", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al confirmar el import.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6" data-testid="ops-import-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/ops/conciliacion")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Importar movimientos bancarios</h1>
          <p className="text-sm text-slate-500">BBVA, CLIP, Rappi, Uber Eats, DiDi, Oxxo Pay</p>
        </div>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 text-sm">
        {["Seleccionar archivo", "Revisar preview", "Confirmado"].map((label, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              step > idx + 1 ? "bg-green-500 text-white" :
              step === idx + 1 ? "bg-[#FFDF00] text-slate-900" : "bg-slate-200 text-slate-500"
            }`}>{step > idx + 1 ? "✓" : idx + 1}</div>
            <span className={step === idx + 1 ? "font-medium text-slate-900" : "text-slate-400"}>{label}</span>
            {idx < 2 && <ChevronRight className="w-3 h-3 text-slate-300" />}
          </div>
        ))}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Canal / fuente</label>
            <select
              value={channel}
              onChange={e => setChannel(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
              data-testid="channel-select"
            >
              {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Archivo ({selectedChannel?.accept})
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                file ? "border-green-300 bg-green-50" : "border-slate-200 hover:border-yellow-300 hover:bg-yellow-50"
              }`}
              data-testid="file-drop-zone"
            >
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileText className="w-8 h-8 text-green-600" />
                  <span className="font-medium text-green-700">{file.name}</span>
                  <span className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-slate-400" />
                  <span className="text-slate-600 font-medium">Click para seleccionar archivo</span>
                  <span className="text-xs text-slate-400">{selectedChannel?.accept}</span>
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={selectedChannel?.accept}
              className="hidden"
              onChange={e => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className="w-full py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors disabled:opacity-50"
            data-testid="btn-procesar-archivo"
          >
            {loading ? "Procesando..." : "Procesar archivo"}
          </button>
        </div>
      )}

      {step === 2 && preview && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Resumen del import</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-2xl font-bold text-slate-900">{preview.summary?.total_rows}</div>
                <div className="text-xs text-slate-500">Filas totales</div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-700">{preview.summary?.movements_found}</div>
                <div className="text-xs text-slate-500">Movimientos a importar</div>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-700">{preview.summary?.skipped}</div>
                <div className="text-xs text-slate-500">Omitidos</div>
              </div>
            </div>
            {preview.summary?.warnings?.length > 0 && (
              <div className="mt-3 p-3 bg-yellow-50 rounded-lg space-y-1">
                {preview.summary.warnings.map((w, i) => (
                  <div key={i} className="text-xs text-yellow-700 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {w}
                  </div>
                ))}
              </div>
            )}
          </div>

          {preview.preview?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Preview (primeros 10)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="pb-1 text-left text-slate-500">Fecha</th>
                      <th className="pb-1 text-right text-slate-500">Monto neto</th>
                      <th className="pb-1 text-left text-slate-500">Referencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((m, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-1">{m.movement_date}</td>
                        <td className="py-1 text-right font-mono">${(m.amount_net || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                        <td className="py-1 text-slate-500 truncate max-w-xs">{m.reference}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => setStep(1)}
              className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              Volver
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="flex-1 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors disabled:opacity-50"
              data-testid="btn-confirmar-import"
            >
              {loading ? "Importando..." : `Confirmar import (${preview.summary?.movements_found} mov.)`}
            </button>
          </div>
        </div>
      )}

      {step === 3 && result && (
        <div className="bg-white rounded-xl border border-green-200 p-8 text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
          <h3 className="text-lg font-bold text-slate-900">Import completado</h3>
          <p className="text-slate-600">
            Se importaron <strong>{result.inserted}</strong> movimientos de <strong>{channel}</strong>.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate("/ops/conciliacion")}
              className="px-6 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors"
            >
              Ver conciliaciones
            </button>
            <button
              onClick={() => { setStep(1); setFile(null); setPreview(null); setResult(null); }}
              className="px-6 py-2.5 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              Importar otro archivo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
